/**
 * Hook del carrito — Portal Cliente Menuee
 *
 * Responsabilidad: estado local de ítems normalizados listos para E6 (vía pedidoMapper).
 *
 * Qué nunca debe hacer:
 * - Persistir subtotal, total, delivery, propinas ni precioTotal por línea.
 * - Consultar Supabase ni invocar RPC.
 * - Almacenar producto_id legacy.
 */

import { useState, useCallback } from "react";
import {
  resolverProductoSucursalId,
  muestraSeccionExtras,
  isProductoConTamanos,
  esComentarioArmadoValido,
  parsearComentarioOpciones,
  formatearNombreConTamano,
  resolverTamanoElegido,
  calcularPrecioItemArmable,
} from "../mappers/productoMapper";

/**
 * @typedef {import('../mappers/productoMapper').ProductoLegacy} ProductoLegacy
 */

/**
 * @typedef {Object} CarritoItem
 * @property {string} productoSucursalId
 * @property {number} cantidad
 * @property {string|null} comentario
 * @property {string} nombre
 * @property {string} [nombreBase]
 * @property {number} precio
 * @property {string|null} imagen
 * @property {string} categoria
 * @property {ProductoLegacy[]} [extras]
 * @property {boolean} [mostrar_extras]
 * @property {boolean} [armable]
 * @property {number} [armable_cantidad]
 * @property {{ id: string, nombre: string, precio?: number, orden: number }[]} [ingredientes]
 * @property {boolean} [ingredientes_con_precio]
 * @property {boolean} [con_tamanos]
 * @property {{ id: string, nombre: string, precio: number, orden: number }[]} [tamanos]
 * @property {string|null} [tamanoId]
 * @property {string|null} [tamanoNombre]
 * @property {number} [precioBase]
 */

/**
 * Construye un ítem de carrito desde un producto de catálogo (productoMapper).
 *
 * @param {ProductoLegacy} producto
 * @param {string|null} [comentario=null]
 * @param {ProductoLegacy[]} [extras=[]]
 * @param {{ tamano?: { id?: string, nombre?: string, precio?: number }|null, ingredientesElegidos?: { nombre?: string, cantidad?: number }[] }} [opciones={}]
 * @returns {CarritoItem}
 */
export function buildCarritoItemFromCatalogo(
  producto,
  comentario = null,
  extras = [],
  opciones = {}
) {
  const productoSucursalId = resolverProductoSucursalId(producto);
  if (!productoSucursalId) {
    throw new Error("buildCarritoItemFromCatalogo: falta productoSucursalId");
  }

  const precioCatalogo = Number(producto?.precio);
  const precioCatalogoSeguro = Number.isFinite(precioCatalogo) ? precioCatalogo : 0;
  const comentarioNormalizado =
    comentario != null && String(comentario).trim() !== ""
      ? String(comentario).trim()
      : null;

  const armable = muestraSeccionExtras(producto);
  const conTamanos = isProductoConTamanos(producto);
  const tamano = conTamanos
    ? resolverTamanoElegido(producto, opciones.tamano)
    : null;

  if (conTamanos && !tamano) {
    throw new Error("buildCarritoItemFromCatalogo: el producto requiere un tamaño");
  }
  if (conTamanos && !parsearComentarioOpciones(comentarioNormalizado).tamano) {
    throw new Error("buildCarritoItemFromCatalogo: el tamaño debe ir en el comentario");
  }
  if (armable && !esComentarioArmadoValido(comentarioNormalizado, producto.armable_cantidad)) {
    throw new Error("buildCarritoItemFromCatalogo: el armado supera el máximo");
  }

  const nombreBase = String(producto?.nombre || "").trim();
  const nombre = formatearNombreConTamano(nombreBase, tamano?.nombre);
  const ingredientesElegidos =
    opciones.ingredientesElegidos !== undefined
      ? opciones.ingredientesElegidos
      : parsearComentarioOpciones(comentarioNormalizado).armado;
  const precioBase = Number(
    tamano ? tamano.precio : precioCatalogoSeguro
  );
  const precio = calcularPrecioItemArmable(producto, {
    tamano,
    ingredientesElegidos: armable ? ingredientesElegidos : [],
  });

  return {
    productoSucursalId,
    cantidad: 1,
    comentario: comentarioNormalizado,
    nombre,
    nombreBase,
    precio,
    precioBase: Number.isFinite(precioBase) ? precioBase : precio,
    imagen: producto?.imagen_url ?? producto?.img ?? null,
    categoria: String(producto?.categoria || ""),
    extras: (extras || []).filter((extra) => resolverProductoSucursalId(extra)),
    mostrar_extras: producto?.mostrar_extras === true,
    armable,
    armable_cantidad: armable ? producto.armable_cantidad : 0,
    ingredientes: armable ? producto.ingredientes : [],
    ingredientes_con_precio: armable
      ? producto.ingredientes_con_precio === true
      : false,
    con_tamanos: conTamanos,
    tamanos: conTamanos ? producto.tamanos : [],
    tamanoId: tamano?.id ?? null,
    tamanoNombre: tamano?.nombre ?? null,
  };
}

/**
 * El producto principal y cada producto de la categoría Extras entran
 * como líneas normales. Los extras no modifican el precio de la línea principal.
 *
 * @param {ProductoLegacy} producto
 * @param {{ comentario?: string|null, extras?: ProductoLegacy[], tamano?: Object|null, ingredientesElegidos?: { nombre?: string, cantidad?: number }[] }} [opciones]
 * @returns {{ producto: ProductoLegacy, opciones: Object }[]}
 */
export function planAgregadoAlCarrito(producto, opciones = {}) {
  const extras = Array.isArray(opciones?.extras) ? opciones.extras : [];
  return [
    {
      producto,
      opciones: { ...opciones, extras: [] },
    },
    ...extras.map((extra) => ({
      producto: extra,
      opciones: {},
    })),
  ];
}

function firmaExtras(item) {
  return (item.extras || [])
    .map((extra) => resolverProductoSucursalId(extra))
    .filter(Boolean)
    .sort()
    .join("|");
}

/**
 * @param {CarritoItem} item
 * @param {CarritoItem} otro
 * @returns {boolean}
 */
function mismaLineaCarrito(item, otro) {
  return (
    item.productoSucursalId === otro.productoSucursalId &&
    (item.comentario || null) === (otro.comentario || null) &&
    firmaExtras(item) === firmaExtras(otro)
  );
}

export function useCarrito() {
  const [carrito, setCarrito] = useState(/** @type {CarritoItem[]} */ ([]));

  const agregar = useCallback((producto, opciones = {}) => {
    const comentario =
      opciones.comentario !== undefined
        ? opciones.comentario
        : producto?.comentario ?? null;
    const extras =
      opciones.extras !== undefined
        ? opciones.extras
        : producto?.extras ?? [];

    let nuevo;
    try {
      nuevo = buildCarritoItemFromCatalogo(producto, comentario, extras, {
        tamano: opciones.tamano || null,
        ingredientesElegidos: opciones.ingredientesElegidos,
      });
    } catch {
      return;
    }

    setCarrito((prev) => {
      const indice = prev.findIndex((item) => mismaLineaCarrito(item, nuevo));
      if (indice === -1) {
        return [...prev, nuevo];
      }
      return prev.map((item, i) =>
        i === indice ? { ...item, cantidad: item.cantidad + 1 } : item
      );
    });
  }, []);

  const eliminar = useCallback((idx) => {
    setCarrito((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const actualizarComentario = useCallback((idx, comentario) => {
    const valor =
      comentario != null && String(comentario).trim() !== ""
        ? String(comentario).trim()
        : null;

    setCarrito((prev) =>
      prev.map((item, i) => {
        if (i !== idx) {
          return item;
        }
        if (
          (item.armable &&
            !esComentarioArmadoValido(valor, item.armable_cantidad)) ||
          (item.con_tamanos && !parsearComentarioOpciones(valor).tamano)
        ) {
          return item;
        }
        return { ...item, comentario: valor };
      })
    );
  }, []);

  const actualizarItemDesdeCatalogo = useCallback((idx, producto, opciones = {}) => {
    const comentario =
      opciones.comentario !== undefined
        ? opciones.comentario
        : producto?.comentario ?? null;
    const extras =
      opciones.extras !== undefined
        ? opciones.extras
        : producto?.extras ?? [];

    let actualizado;
    try {
      actualizado = buildCarritoItemFromCatalogo(producto, comentario, extras, {
        tamano: opciones.tamano || null,
        ingredientesElegidos: opciones.ingredientesElegidos,
      });
    } catch {
      return;
    }

    setCarrito((prev) =>
      prev.map((item, i) =>
        i === idx ? { ...actualizado, cantidad: item.cantidad } : item
      )
    );
  }, []);

  const actualizarCantidad = useCallback((idx, cantidad) => {
    const cantidadNum = Number(cantidad);
    if (!Number.isFinite(cantidadNum) || cantidadNum < 1) {
      return;
    }

    setCarrito((prev) =>
      prev.map((item, i) =>
        i === idx ? { ...item, cantidad: Math.floor(cantidadNum) } : item
      )
    );
  }, []);

  const vaciar = useCallback(() => {
    setCarrito([]);
  }, []);

  return {
    carrito,
    agregar,
    eliminar,
    actualizarComentario,
    actualizarItemDesdeCatalogo,
    actualizarCantidad,
    vaciar,
  };
}
